import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { WeddingHero } from "./WeddingHero";
import { WeddingReviewsStrip } from "./WeddingReviewsStrip";
import { WeddingProductCard } from "./WeddingProductCard";
import { groupProducts, WEDDING_POSTS } from "./weddingGroups";
import { WeddingToolsStrip } from "../../components/WeddingToolsStrip";
import { SubcategoryGuide } from "../../components/SubcategoryGuide";

function GroupSection({ group }) {
  return (
    <section id={group.id} className="scroll-mt-28" data-testid={`wedding-group-${group.id}`}>
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 mb-8">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-kolt-orange" />
            <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-kolt-orange">{group.eyebrow}</span>
          </div>
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-[#2C3340] leading-tight">{group.title}</h2>
        </div>
        {group.intro && <p className="lg:col-span-8 text-base text-slate-600 leading-relaxed lg:pt-8 max-w-3xl">{group.intro}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {group.items.map((p) => <WeddingProductCard key={p.product_id} product={p} />)}
      </div>
    </section>
  );
}

function WeddingReads() {
  return (
    <section className="bg-white border border-[#E8D9C3] p-6 md:p-8" data-testid="wedding-reads">
      <div className="flex items-center gap-3 mb-5">
        <BookOpen className="w-5 h-5 text-kolt-orange" />
        <h2 className="font-display font-black text-xl md:text-2xl tracking-tight text-[#2C3340]">Wedding stationery guides from our press</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {WEDDING_POSTS.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-slate-200 hover:border-kolt-orange p-4 transition-colors" data-testid={`wedding-read-${p.slug}`}>
            <div className="font-semibold text-[#2C3340] leading-snug">{p.title}</div>
            <div className="mt-3 font-mono text-[10px] tracking-widest uppercase text-kolt-orange inline-flex items-center gap-1">Read <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function WeddingCollection({ products, loading, serviceTabs }) {
  const groups = useMemo(() => groupProducts(products), [products]);
  return (
    <main className="bg-[#FBF8F3] min-h-screen pb-20" data-testid="shop-page">
      <WeddingHero groups={groups} count={products.length} />
      <WeddingReviewsStrip />
      {serviceTabs}
      <div className="container-page py-10 space-y-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => <div key={i} className="bg-white border border-[#E8D9C3] animate-pulse h-[420px]" />)}
          </div>
        ) : (
          groups.map((g) => <GroupSection key={g.id} group={g} />)
        )}
        <WeddingToolsStrip />
        <WeddingReads />
        <SubcategoryGuide subSlug="wedding-collateral" />
      </div>
    </main>
  );
}
